import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, absoluteUrl } from "@/lib/api";
import { ArrowLeft, ArrowUpRight, ImageSquare } from "@phosphor-icons/react";
import { labelFor } from "@/components/PortfolioSection";

export default function PortfolioDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/portfolio/${id}`);
        setItem(data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#050505] text-white p-12">Loading…</div>;

  if (notFound || !item) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6">
        <ImageSquare size={48} className="text-[#a3a3a3] mb-4" />
        <h1 className="font-display text-3xl mb-3">Not found</h1>
        <p className="text-[#a3a3a3] mb-6">This work isn't published or doesn't exist.</p>
        <Link to="/" className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider">Back home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#262626]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/portfolio" className="flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white" data-testid="back-home">
            <ArrowLeft size={16} /> All work
          </Link>
          <Link to="/login" className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-white">
            Client portal →
          </Link>
        </div>
      </header>

      <article className="max-w-[1100px] mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="flex items-center gap-3 hero-fade-up hero-delay-1">
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{labelFor(item.service_type)}</span>
          {item.platform && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D] text-[#FF2D2D]" data-testid="work-platform">
              Built on {item.platform}
            </span>
          )}
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3 hero-fade-up hero-delay-2" data-testid="work-title">{item.title}</h1>
        {item.client_name && (
          <div className="text-sm text-[#a3a3a3] mt-3 uppercase tracking-[0.2em] hero-fade-up hero-delay-3">For {item.client_name}</div>
        )}

        {item.cover_url && (
          <div className="mt-10 border border-[#262626] bg-[#0a0a0a] hero-fade-in hero-delay-4">
            <img src={`${absoluteUrl(item.cover_url)}?w=1600`} alt={item.title} loading="eager" decoding="async" className="w-full h-auto" data-testid="work-cover" />
          </div>
        )}

        {item.description && (
          <div className="mt-10 max-w-[680px] text-base md:text-lg text-[#cfcfcf] leading-relaxed whitespace-pre-wrap" data-testid="work-description">
            {item.description}
          </div>
        )}

        {item.external_url && (
          <a
            href={item.external_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="work-external"
            className="inline-flex items-center gap-2 mt-10 btn-primary px-6 py-3 text-sm font-bold uppercase tracking-wider"
          >
            Visit live site <ArrowUpRight size={16} weight="bold" />
          </a>
        )}

        <div className="mt-16 pt-10 border-t border-[#262626] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-[#a3a3a3]">Like what you see? We're taking on new clients.</div>
          <Link to="/register" className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider" data-testid="cta-start-project">
            Start a project →
          </Link>
        </div>
      </article>
    </div>
  );
}
